import { FC, useEffect, useReducer, useState } from "react";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import useGridFilter, { CreateSelectorGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { checkAccessStatus } from "../../CommonHelperFunctions";
import { LabelPrintScenarioCreatorModal } from "./LabelPrintScenarioCreatorModal";
import { LabelPrintScenarioDataProvider } from "../../../Services/DataProviders/LabelPrintScenarioDataProvider";
import DefaultDictionaryCommandsPanel from "../../../components/commandsPanels/DefaultDictionaryCommandsPanel";
import { ILabelPrintScenarioViewDTO } from "../../../libs/coreapi-dto/dirs/labelPrintScenario";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import { TabsPanel } from "../../../components/tabs";
import classNames from "classnames";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import { initialState, reducer } from "./ReducerEx";
import styles from "../../styles/index.module.scss";
import { usePluginContext } from "../../../system/providers/plugin";
import { DocumentType } from "../../../@types/enumsGlobal";
import { set } from "lodash";

export const LabelPrintScenarioGrid: FC<IGridProps> = (props) => {
    const appCtx = useAppContext()
    const dataProvider = new LabelPrintScenarioDataProvider(appCtx.coreApiService);
    const userContext = useUserContext()
    const [data, setData] = useState<ILabelPrintScenarioViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>();
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(props.baseGridFilter ?? CreateSelectorGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    const pluginCtx = usePluginContext();
    const [state, dispatch] = useReducer(reducer, initialState, (initialState) => {
        return initialState
    });
    
    useEffect(() => {
        setIsSubmitting(true)
        dataProvider.getView(gridFilter, async (data, totalCount) => {
            setData(data)
            setTotalCount(totalCount)
            setLoadingStatus(LoadingStatus.Completed)
            setIsSubmitting(false)
        })
    }, [gridFilter])

    useEffect(() => {
        if (viewState === 'refresh') {
            setIsSubmitting(true)
            dataProvider.getView(gridFilter, async (data, totalCount) => {
                setData(data)
                setViewState('view')
                setTotalCount(totalCount)
                setLoadingStatus(LoadingStatus.Completed)
                setIsSubmitting(false)
            })
        }
    }, [viewState])

    return (
        <PluginWrapper>
            {
                checkAccessStatus(props.plugin.permission as IPermission, userContext.userPermission) && <ToolbarWrapper>
                    <DefaultDictionaryCommandsPanel
                        selectedItem={[selectedItem, setSelectedItem]}
                        gridFilter={[gridFilter, dispatchGridFilter]}
                        dataProvider={dataProvider}
                        creatorModalJsx={LabelPrintScenarioCreatorModal}
                        pluginSettings={props.plugin}
                        setViewState={(vs) => setViewState(vs)}
                    />
                </ToolbarWrapper>
            }
            {
                isSubmitting ? <Spinner />
                    :
                    <>
                        <DefaultGrid
                            showQuantity
                            gridId={props.gridId}
                            data={data}
                            loadingStatus={loadingStatus}
                            setLoadingStatus={setLoadingStatus}
                            separator
                            singleDirectory
                            totalCount={totalCount}
                            filter={gridFilter}
                            dataProvider={dataProvider}
                            plugin={props.plugin}
                            selectedItem={selectedItem}
                            selectedItems={selectedItems}
                            multipleSelect={props.multipleSelect}
                            onDoubleClick={(row) => props.onDoubleClick?.(row)}
                            onSelect={(row) => {
                                setSelectedItem(row)
                                props.onSelect?.(row)
                                dispatch({ type: "setSelectedItem", payload: row });
                                row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.gosContract) : pluginCtx.masterGrid.onUnselectEvent();
                                props.onSelect?.(row ? row : undefined);
                            }}
                            onMultipleSelect={(rows) => {
                                setSelectedItems(rows)
                                props?.onMultipleSelect?.(rows)
                            }}
                            onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                            onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                            onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                            onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            onEnter={() => setViewState("edit")}
                        />
                        {
                            <>
                                <div className={styles.detailsTabView}>
                                    <div className={classNames(styles.separator, styles.separator_horizontal)}></div>
                                    {detailsTabsPanel.tabs.length > 0 && state.selectedItem && (
                                        <>
                                            <div className={tabsStyles.tabsPanelWrapper}>
                                                <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                                    <TabsPanel
                                                        id="detailsTabsPanel"
                                                        activeId={detailsTabsPanel.currentTab?.id}
                                                        tabs={detailsTabsPanel.tabs}
                                                        onActive={(id) => {
                                                            dispatchDetailsTabsPanel({ type: "activate", payload: id });
                                                        }}
                                                    ></TabsPanel>
                                                </div>
                                            </div>
                                            <div className={tabsStyles.contentWrapper}>
                                                {detailsTabsPanel.tabs.map((item) => {
                                                    return (
                                                        <div key={item.id} className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                            {item.view.content}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        }
                    </>
            }
        </PluginWrapper>
    )
}